// botActions.js
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { ref, update, get } from "firebase/database";
import { rtdb } from "../firebase/config";
// import { useRoomsStore } from "../store/useRoomsStore"; // ✅ grab the hook
import {
  getGameplayMatchSnapshotRTDB,
  savePlayerDeckAndHand,
  updateHandAndDeck,
  updateHandAndDeckRTDB,
  savePlayerDeckAndHandRTDB,
} from "../utils/roomFunctions";
import {
  parseCardEffect,
  resolveCurseAbsorptionHealthAmount,
  isSpecialtyLikeCard,
} from "./deckEngine";
import { buildOngoingCardBuff } from "./cardEffectBuffs";
import _ from "lodash";
import {
  specialtyCardEffect,
  getElementalBonus,
  maybeActivateCurseAbsorptionFromHand,
  normalizeEffectArea,
  toFiniteNumber,
} from "../utils/Utils";
import { GAME_MODES, getModeFromRoom } from "./modes";

// const { hands, decks } = useRoomsStore.getState();

export const getBotDelay = () => Math.floor(Math.random() * 5000) + 5000;
export const getRandomItem = (arr) =>
  arr[Math.floor(Math.random() * arr?.length)];
export const delay = (ms) => new Promise((res) => setTimeout(res, ms));
export const getNextLivingPlayerIndex = (players, currentIndex) => {
  console.warn("Players", players.length, "Current INdex", currentIndex);
  const total = players?.length;
  let i = currentIndex;

  for (let count = 0; count < total; count++) {
    i = (i + 1) % total;
    const candidate = players[i];
    if (!candidate.character.isDead) {
      return i;
    }
  }

  // fallback (shouldn't happen)
  return currentIndex;
};

export const stripHandAndDeckFromPlayers = (players) => {
  return [...players].map(({ hand, deck, ...rest }) => ({ ...rest }));
};

const isAlivePlayer = (player) => {
  if (!player?.character) return false;
  if (player.character.isDead) return false;
  const health = Number(player.character.health);
  if (Number.isFinite(health) && health <= 0) return false;
  return true;
};

const removeOneCardInstance = (cards = [], targetCard) => {
  const next = Array.isArray(cards) ? [...cards] : [];
  if (!targetCard || !next.length) return next;

  let idx = -1;
  if (targetCard._uuid) {
    idx = next.findIndex((c) => c?._uuid === targetCard._uuid);
  }
  if (idx < 0) {
    idx = next.findIndex(
      (c) =>
        c?.id === targetCard?.id &&
        c?.name === targetCard?.name &&
        c?.type === targetCard?.type
    );
  }
  if (idx < 0) {
    idx = next.findIndex((c) => c?.id === targetCard?.id);
  }
  if (idx < 0) return next;
  next.splice(idx, 1);
  return next;
};

const getNormalizedCardName = (card) =>
  String(card?.name || "").trim().toLowerCase();

const getCardAffectedAreas = (card) => {
  if (Array.isArray(card?.areaAffected)) return card.areaAffected;
  if (Array.isArray(card?.affectedAreas)) return card.affectedAreas;
  return [];
};

const isMirrorShieldCard = (card) =>
  ["mirror shield", "mirror sheild"].includes(getNormalizedCardName(card));

const readPrivateCardsForPlayerRTDB = async (roomId, playerId) => {
  if (!roomId || !playerId) {
    return { hand: [], deck: [] };
  }

  const [handSnap, deckSnap] = await Promise.all([
    get(ref(rtdb, `rooms/${roomId}/hands/${playerId}`)),
    get(ref(rtdb, `rooms/${roomId}/decks/${playerId}`)),
  ]);

  return {
    hand: Array.isArray(handSnap.val()?.cards) ? handSnap.val().cards : [],
    deck: Array.isArray(deckSnap.val()?.cards) ? deckSnap.val().cards : [],
  };
};

const ensurePrivateStateForPlayers = async (
  roomId,
  playerIds = [],
  hands = {},
  decks = {},
) => {
  const nextHands = { ...(hands || {}) };
  const nextDecks = { ...(decks || {}) };

  await Promise.all(
    (playerIds || []).filter(Boolean).map(async (playerId) => {
      const hasHand = Array.isArray(nextHands[playerId]);
      const hasDeck = Array.isArray(nextDecks[playerId]);
      if (hasHand && hasDeck) return;

      try {
        const { hand, deck } = await readPrivateCardsForPlayerRTDB(roomId, playerId);
        if (!hasHand) nextHands[playerId] = hand;
        if (!hasDeck) nextDecks[playerId] = deck;
      } catch (error) {
        console.warn("Failed to hydrate bot private cards for player:", {
          playerId,
          error,
        });
      }
    }),
  );

  return { hands: nextHands, decks: nextDecks };
};

const reassignWeight = (
  actions,
  bot,
  hand,
  matchData,
  lastMoveWasStanceChange = false
) => {
  const character = bot.character;
  const updatedActions = [...actions];
  const health = character.health;
  const stance = character.stance;
  const hasDefense = character.defense > 0;

  const healingCard = hand.find(
    (card) =>
      getCardAffectedAreas(card).some(
        (area) => String(area || "").toLowerCase() === "health"
      ) && card.turnsActive === "Instant"
  );

  const buffCard = hand.find((card) =>
    getCardAffectedAreas(card).some((area) =>
      ["attack", "defense", "accuracy"].includes(area.toLowerCase())
    )
  );

  const lowHealth = health <= 3;
  const highHealth = health >= 6;

  // Reset weights
  updatedActions.forEach((a) => (a.weight = 25));

  // 🛡️ Can't attack if in defense
  if (stance === "defense") {
    const attackAction = updatedActions.find((a) => a.type === "attack");
    if (attackAction) attackAction.weight = 0;
  }

  // 🧠 Don't attack if you *just* attacked
  const lastMove = matchData?.moves?.at(-1);
  const didJustAttack =
    lastMove?.player === bot.name && lastMove.type.includes("attack");

  if (didJustAttack) {
    const attackAction = updatedActions.find((a) => a.type === "attack");
    if (attackAction) attackAction.weight = 0;
  }

  // ⚠️ LOW HEALTH MODE (somewhat defensive)
  if (lowHealth) {
    if (stance === "attack" && hasDefense && !lastMoveWasStanceChange) {
      updatedActions.find((a) => a.type === "stance").weight += 10;
    }
    if (healingCard) {
      updatedActions.find((a) => a.type === "playCard").weight += 15;
    } else {
      updatedActions.find((a) => a.type === "tradeCard").weight += 5;
    }
  }

  // 💪 HIGH HEALTH MODE
  if (highHealth) {
    if (stance === "defense" && !lastMoveWasStanceChange) {
      updatedActions.find((a) => a.type === "stance").weight += 20;
    }
    if (stance === "attack") {
      if (!lastMoveWasStanceChange)
        updatedActions.find((a) => a.type === "stance").weight = 0;
      const attackAction = updatedActions.find((a) => a.type === "attack");
      if (attackAction) attackAction.weight += 20;
    }
  }

  // 🧠 Already in attack stance? Favor attacking
  if (stance === "attack") {
    updatedActions.find((a) => a.type === "attack").weight += 20;
    updatedActions.find((a) => a.type === "playCard").weight += buffCard
      ? 10
      : 0;
  }

  // 🧃 No healing or buffs? Trade it up
  if (!healingCard && !buffCard) {
    updatedActions.find((a) => a.type === "tradeCard").weight += 5;
  }

  return updatedActions;
};

// const reassignWeight = (
//   actions,
//   bot,
//   hand,
//   matchData,
//   lastMoveWasStanceChange = false,
//   nextPlayer = null,
//   isPVE = false
// ) => {
//   let updatedActions = [...actions];
//   const stance = bot.character.stance;
//   const health = bot.character.health;
//   const defense = bot.character.defense;
//   const accuracy = bot.character.accuracy;

//   let healingCard
//   let buffAccuracyCard
//   let buffDefenseCard

//   const hasTradeableCards =
//     hand.length >= 3 && !healingCard && !buffAccuracyCard && !buffDefenseCard;

//   const lowHealth = health <= 4;
//   const highHealth = health >= 6;
//   const lowAccuracy = accuracy <= 4;
//   const inDefense = stance === "defense";
//   const inAttack = stance === "attack";

//   // 🔁 Reset all weights
//   updatedActions.forEach((a) => (a.weight = 25));

//   // ❌ Rule 1: Don’t change stance if already changed this turn
//   if (lastMoveWasStanceChange) {
//     updatedActions.find((a) => a.type === "stance").weight = 0;
//   }

//   // ❤️ Rule 2: Low Health + Heal Card → Heal
//   if (lowHealth && healingCard) {
//     updatedActions.find((a) => a.type === "playCard").weight += 30;
//   }

//   // 🛡️ Rule 3: Low Health + Has Defense → Heal or Buff Defense
//   if (lowHealth && defense > 0) {
//     if (!inDefense)
//       updatedActions.find((a) => a.type === "stance").weight += 15;
//     if (buffDefenseCard)
//       updatedActions.find((a) => a.type === "playCard").weight += 20;
//     if (healingCard)
//       updatedActions.find((a) => a.type === "playCard").weight += 20;
//   }

//   // 🎯 Rule 4: Low Accuracy → Boost It
//   if (lowAccuracy && buffAccuracyCard) {
//     updatedActions.find((a) => a.type === "playCard").weight += 25;
//   }

//   // ❌ Rule 5: Cannot Attack in Defense Stance
//   const attackAction = updatedActions.find((a) => a.type === "attack");
//   if (inDefense && attackAction) {
//     attackAction.weight = 0;
//   }

//   // 🧨 NEW: Add affliction card preference if in attack stance
//   if (afflictionCard && inAttack) {
//     updatedActions.find((a) => a.type === "playCard").weight += 15;
//   }

//   // 🗡️ Rule 6: Attack Weak Enemies (no defense)
//   const enemyNoDefense = matchData.players?.some(
//     (p) => p.id !== bot.id && !p.character.isDead && p.character.defense === 0
//   );
//   if (inAttack && enemyNoDefense && attackAction) {
//     attackAction.weight += 25;
//   }

//   // ☠️ Rule 7: If any enemy can be killed, prioritize attack
//   const killableEnemy = matchData.players?.find(
//     (p) =>
//       p.id !== bot.id &&
//       !p.character.isDead &&
//       p.character.health + p.character.defense <= bot.character.attack
//   );
//   if (killableEnemy && attackAction) {
//     attackAction.weight += 30;
//   }

//   // 🛡️ Rule 8: High health, no defense → Buff up
//   if (highHealth && defense === 0) {
//     if (buffDefenseCard)
//       updatedActions.find((a) => a.type === "playCard").weight += 20;
//     if (!inDefense)
//       updatedActions.find((a) => a.type === "stance").weight += 15;
//   }

//   // 🔥 Rule 9: High health + high accuracy in defense → go aggressive
//   if (highHealth && accuracy >= 6 && inDefense) {
//     updatedActions.find((a) => a.type === "stance").weight += 10;
//   }

//   // 🧠 Rule 10: Prep for next player
//   if (nextPlayer?.character) {
//     const nextIsThreat =
//       nextPlayer.character.attack >= 5 && nextPlayer.character.accuracy >= 6;

//     if (nextIsThreat && defense < 3) {
//       updatedActions.find((a) => a.type === "stance").weight += 10;
//       if (buffDefenseCard)
//         updatedActions.find((a) => a.type === "playCard").weight += 10;
//     }
//   }

//   // 🤝 Rule 11: PVE + bot is next → Attack more
//   if (isPVE && nextPlayer?.isBot && attackAction) {
//     attackAction.weight += 10;
//   }

//   // ♻️ Rule 12: Trade junk
//   if (hasTradeableCards) {
//     updatedActions.find((a) => a.type === "tradeCard").weight += 20;
//   }

//   return updatedActions;
// };

export const simulateBotTurn = async (
  botPlayer,
  allPlayers,
  matchData,
  roomId,
  syncMatchData,
  initDelay = true,
  //   nextTurn,
  hands,
  decks,
  hasStartedBotTurnRef,
  commitMatchMutation = null
) => {
  console.log("🤖 Bot is thinking (from simulateBotTurn function)...");
  const matchRef = ref(rtdb, `rooms/${roomId}/matchData/match`);

  console.warn("ALL PLAYERS", allPlayers);
  console.warn("BOT PLAYER", botPlayer);
  console.warn("MATCH DATA", matchData);
  console.warn("HANDS", hands);

  if (!allPlayers || !botPlayer || !matchData) {
    return;
  }

  let currentPlayerIndex = -1;

  let MIN_THINKING_TIME = 3500;
  let forceAttackDelay = false;
  const thinkStart = Date.now();

  const lastMove = matchData?.moves[matchData?.moves?.length - 1] || null;
  if (lastMove) {
    const { type, attacker, player, target, timestamp, card } = lastMove;
    if (type === "attack") {
      MIN_THINKING_TIME = 5500;
      forceAttackDelay = true;
    }
  }

  try {
    // let playersCopy = [...allPlayers];
    let playersCopy = allPlayers.map((p) => _.cloneDeep(p)); //! Testing

    currentPlayerIndex = playersCopy.findIndex((p) => p.id === botPlayer.id);
    if (currentPlayerIndex === -1) {
      console.warn("🚨 Bot not found in playersCopy:", botPlayer.name);
      return;
    }

    // Don't double-wait if initDelay already handled it
    if (initDelay || forceAttackDelay) {
      await delay(MIN_THINKING_TIME);
    } else {
      const elapsed = Date.now() - thinkStart;
      const remaining = MIN_THINKING_TIME - elapsed;
      if (remaining > 0) await delay(remaining);
    }

    console.log("after the delay");

    const refreshedData = await getGameplayMatchSnapshotRTDB(roomId, {
      includeLegacyFallback: true,
      requirePlayers: true,
    });
    if (
      !refreshedData ||
      refreshedData?.status === "finished" ||
      refreshedData?.winner
    ) {
      return;
    }
    matchData = refreshedData;
    playersCopy = refreshedData?.players?.map((p) => _.cloneDeep(p));
    if (!Array.isArray(playersCopy) || playersCopy.length === 0) return;
    let bot = playersCopy.find((p) => p.id === botPlayer.id); // safer
    if (!bot) return;

    const hydratedPrivateState = await ensurePrivateStateForPlayers(
      roomId,
      playersCopy.map((p) => p?.id).filter(Boolean),
      hands,
      decks,
    );
    hands = hydratedPrivateState.hands;
    decks = hydratedPrivateState.decks;

    let hand = Array.isArray(hands?.[bot.id]) ? hands[bot.id] : [];
    const deck = Array.isArray(decks?.[bot.id]) ? decks[bot.id] : [];

    if (!hand.length && !deck.length) {
      console.warn("Bot private state missing after RTDB hydration:", bot.id);
      return;
    }

    // console.log("Current index => ", currentPlayerIndex);
    // console.log(bot.name);

    //   const cpMovesLeft = latestData?.cpMovesLeft;
    const currentPlayer = refreshedData?.currentPlayerTurn;

    if (currentPlayer !== currentPlayerIndex) return;

    let actions = [
      { type: "attack", weight: 25 },
      { type: "stance", weight: 25 },
      { type: "playCard", weight: 25 },
      { type: "tradeCard", weight: 25 },
    ];

    const lastMove = refreshedData?.moves?.at(-1);
    let lastMoveWasStanceChange =
      lastMove?.type === "stanceChange" && lastMove?.player === bot?.name;
    let nextPlayer = getNextLivingPlayerIndex(playersCopy, currentPlayerIndex);
    const mode = refreshedData?.mode || GAME_MODES.ROYALE;
    const isPVE = mode === GAME_MODES.PVE;
    const isTeams = mode === GAME_MODES.TEAMS;

    const isEnemyForBot = (p) => {
      if (!p || p.id === bot.id) return false;
      if (!isAlivePlayer(p)) return false;
      if (p.character?.cloakedTurns > 0) return false;

      if (isPVE) {
        // PVE: bots vs humans
        return !p.isBot;
      }

      if (isTeams && bot.team && p.team) {
        // TEAM MODE: enemy if on different team
        return p.team !== bot.team;
      }

      // FFA / fallback
      return true;
    };

    const isAllyForBot = (p) => {
      if (!isAlivePlayer(p)) return false;

      // Always treat self as ally
      if (p.id === bot.id) return true;

      if (isPVE) {
        // PVE: other bots are allies
        return p.isBot;
      }

      if (isTeams && bot.team && p.team) {
        // TEAM MODE: same team allies
        return p.team === bot.team;
      }

      // FFA: only self is an ally for aid cards
      return false;
    };

    let newlyWeightedActions = reassignWeight(
      actions,
      bot,
      hand,
      refreshedData,
      lastMoveWasStanceChange
      //   nextPlayer,
      //   isPVE
    );

    if (lastMoveWasStanceChange) {
      newlyWeightedActions.find((a) => a.type === "stance").weight = 0;
      if (bot.character.stance === "attack" && bot.character.defense === 0) {
        newlyWeightedActions.find((a) => a.type === "stance").weight = 0;
      }
      if (bot.character.stance === "defense") {
        newlyWeightedActions.find((a) => a.type === "playCard").weight += 15;
      }
    }

    const totalWeight = newlyWeightedActions.reduce(
      (sum, a) => sum + a.weight,
      0
    );
    const weightedChoice = Math.random() * totalWeight;

    let runningSum = 0;
    const selectedAction = newlyWeightedActions.find((action) => {
      runningSum += action.weight;
      return weightedChoice <= runningSum;
    });

    // Safety fallback (shouldn’t really happen)
    const finalActionType = selectedAction?.type || "tradeCard";

    console.log("Bot is about to take action =>", finalActionType);

    let actionSucceeded = false;

    switch (finalActionType) {
      case "attack": {
        console.warn("new is pve", isPVE, allPlayers);
        const enemies = playersCopy.filter(isEnemyForBot);
        console.warn("BEFORE RANDOM ITEM SELECTION =>", enemies);
        console.warn("PLAYERS COPY", playersCopy);
        const target = getRandomItem(enemies);
        if (target && bot.character.stance === "attack") {
          actionSucceeded = true;
          await simulateBotAttack(
            roomId,
            bot,
            target,
            hands,
            decks,
            playersCopy,
            syncMatchData,
            matchRef
          );
        } else {
          // console.log("⚠️ Bot is not in attack stance, can't attack.");
        }
        break;
      }

      case "stance": {
        const botIndex = playersCopy.findIndex((p) => p.id === bot.id);
        let updatedBot = { ...bot };

        updatedBot.character.stance =
          updatedBot.character.stance === "attack" ? "defense" : "attack";

        playersCopy[botIndex] = updatedBot;

        actionSucceeded = true;

        const move = {
          type: "stanceChange",
          player: bot.name,
          newStance: updatedBot.character.stance,
          timestamp: Date.now(),
        };

        console.log("👉 playersCopy before stance:", playersCopy);
        let updatedPlayersCopy = stripHandAndDeckFromPlayers(playersCopy);
        console.log("👉 Updated playersCopy after trade:", updatedPlayersCopy);

        await syncMatchData(bot.id, updatedPlayersCopy, move);
        await delay(500);
        break;
      }

      case "playCard": {
        let success = false;
        const maxAttempts = 3;
        let attemptCount = 0;

        while (!success && attemptCount < maxAttempts) {
          const playableCard = hand.find((card) =>
            getCardAffectedAreas(card).some((area) =>
              ["health", "attack", "accuracy", "defense"].includes(
                area.toLowerCase()
              )
            )
          );

          if (playableCard) {
            actionSucceeded = true;
            // console.warn(
            //   "BEFORE RANDOM ITEM SELECTION PLAY CARD =>",
            //   allPlayers,
            //   "IS PVE =>",
            //   isPVE
            // );

            let target = null;

            const cardData = parseCardEffect(playableCard);
            const allyCandidates = playersCopy.filter(isAllyForBot);
            const enemyCandidates = playersCopy.filter(isEnemyForBot);
            const playableEnemyCandidates = cardData.isAffliction
              ? enemyCandidates.filter(
                  (p) => Number(p.character?.magicBarrierTurns ?? 0) <= 0
                )
              : enemyCandidates;

            if (cardData.isAid) {
              // Aid cards → self / allies
              if (allyCandidates.length > 0) {
                target = getRandomItem(allyCandidates);
              } else {
                // Fallback to self
                target = bot;
              }
            } else {
              // Harmful cards → enemies
              if (playableEnemyCandidates.length > 0) {
                target = getRandomItem(playableEnemyCandidates);
              } else {
                target = null;
              }
            }

            if (target) {
              await simulateBotPlayCard(
                roomId,
                bot,
                playersCopy,
                playableCard,
                syncMatchData,
                target,
                matchRef,
                hands,
                decks,
                mode,
                commitMatchMutation
              );
              success = true;
            }
          }

          attemptCount++;
        }

        // ✅ Default to random card play if nothing else worked
        if (!success) {
          console.warn("RETRY PLAY CARD =>", hand, isPVE);
          console.warn("RETRY PLAY CARD =>", allPlayers);
          const randomCard = getRandomItem(hand);
          let fallbackTarget;

          let cardData = parseCardEffect(randomCard);
          if (cardData.isAid) {
            if (isPVE) {
              fallbackTarget = getRandomItem(
                allPlayers.filter(
                  (p) => p.id === bot.id || (isAlivePlayer(p) && p.isBot)
                )
              );
            } else {
              fallbackTarget = allPlayers.find(
                (p) => p.id === bot.id && isAlivePlayer(p)
              );
            }
          } else {
            if (isPVE) {
              fallbackTarget = getRandomItem(
                allPlayers.filter(
                  (p) =>
                    p.id !== bot.id &&
                    isAlivePlayer(p) &&
                    !p.isBot &&
                    (!cardData.isAffliction ||
                      Number(p.character?.magicBarrierTurns ?? 0) <= 0)
                )
              );
            } else {
              fallbackTarget = allPlayers.find(
                (p) =>
                  p.id !== bot.id &&
                  isAlivePlayer(p) &&
                  (!cardData.isAffliction ||
                    Number(p.character?.magicBarrierTurns ?? 0) <= 0)
              );
            }
          }

          if (randomCard && fallbackTarget) {
            await simulateBotPlayCard(
              roomId,
              bot,
              playersCopy,
              randomCard,
              syncMatchData,
              fallbackTarget,
              matchRef,
              hands,
              decks,
              mode,
              commitMatchMutation
            );
            //   console.log("🔄 Defaulting to random card play.");
          }
        }

        break;
      }

      case "tradeCard": {
        const enemies = allPlayers.filter(
          (p) => p.id !== bot.id && isAlivePlayer(p)
        );
        // console.warn("BEFORE RANDOM ITEM SELECTION PLAY CARD =>", enemies);
        // console.warn("BEFORE RANDOM ITEM SELECTION PLAY CARD =>", allPlayers);
        const target = getRandomItem(enemies);
        const cardToTrade = getRandomItem(hand);
        //   console.log("CARD TO TRADE", cardToTrade);

        console.warn("Trading with => ", target?.name);
        console.warn("Card to Trade", cardToTrade);

        let tradeSuccess = false;

        if (target && cardToTrade) {
          tradeSuccess = await simulateBotTradeCard(
            roomId,
            bot,
            target,
            playersCopy,
            cardToTrade,
            syncMatchData,
            hands,
            decks
          );
          actionSucceeded = tradeSuccess;
        }

        if (!tradeSuccess) {
          // console.log("♻️ Bot fallback: trade failed, rerolling...");
          const otherActions = actions.filter((a) => a.type !== "tradeCard");
          console.warn(
            "BEFORE RANDOM ITEM SELECTION PLAY CARD =>",
            otherActions
          );
          const rerolled = getRandomItem(otherActions);
          selectedAction.type = rerolled.type;
        }

        break;
      }

      default:
        break;
    }

    if (!actionSucceeded) {
      console.warn("Bot had no valid action; forcing a skipped move fallback.");
      const fallbackMove = {
        type: "playerSkipped(auto)",
        player: bot.name,
        reason: "bot_no_valid_action",
        timestamp: Date.now(),
      };
      const cleanPlayers = stripHandAndDeckFromPlayers(playersCopy);
      await syncMatchData(bot.id, cleanPlayers, fallbackMove);
      await delay(500);
      return;
    }

    if (actionSucceeded) {
      console.log("⏳ Waiting for move commit to settle...");
      await delay(1500);
    }
  } catch (error) {
    console.error("Error in botAction:", error);
  }
};

export const simulateBotAttack = async (
  roomId,
  bot,
  target,
  hands,
  decks,
  playersCopy,
  syncMatchData,
  matchRef
) => {
  const botIndex = playersCopy?.findIndex((p) => p.id === bot.id);
  const targetIndex = playersCopy?.findIndex((p) => p.id === target.id);
  if (botIndex === -1 || targetIndex === -1) return;

  const attacker = { ...bot };
  const defender = { ...target };

  const attackerElement = attacker?.character?.element;
  const defenderElement = defender?.character?.element;
  const baseAccuracy = Number(attacker?.character?.accuracy || 0);
  const totalAccuracy =
    baseAccuracy +
    Number(
      attacker?.character?.buffs?.accuracy?.reduce(
        (acc, buff) => acc + Number(buff?.amt || 0),
        0
      ) || 0
    );
  const totalAttack =
    Number(attacker?.character?.attack || 0) +
    Number(
      attacker?.character?.buffs?.attack?.reduce(
        (acc, buff) => acc + Number(buff?.amt || 0),
        0
      ) || 0
    );

  const roll = Math.ceil(Math.random() * 10);
  let totalDamage = 0;
  let criticalHit = false;
  let elementalBonus = 0;

  if (totalAccuracy >= roll) {
    totalDamage = totalAttack;
    if (baseAccuracy === roll) {
      totalDamage += 1;
      criticalHit = true;
    }
    if (attackerElement && defenderElement) {
      elementalBonus = Number(
        getElementalBonus(attackerElement, defenderElement) || 0
      );
      totalDamage += elementalBonus;
    }

    // 🪞 Mirror Shield check (defender's hand)
    const defenderHand = hands?.[defender.id] || [];
    const mirrorIdx = defenderHand.findIndex(isMirrorShieldCard);

    if (mirrorIdx !== -1) {
      // 1) Destroy shield, write defender hand to RTDB immediately
      const newDefenderHand = defenderHand.filter((_, i) => i !== mirrorIdx);
      const defenderDeck = decks?.[defender.id] || [];
      await updateHandAndDeckRTDB(
        roomId,
        defender.id,
        newDefenderHand,
        defenderDeck
      );

      // 2) Reflect damage to ATTACKER (stance-aware)
      const recvChar = attacker.character;
      if (recvChar.stance === "defense") {
        if (recvChar.defense >= totalDamage) {
          recvChar.defense -= totalDamage;
        } else {
          const remaining = totalDamage - recvChar.defense;
          recvChar.defense = 0;
          recvChar.stance = "attack";
          recvChar.health -= remaining;
        }
      } else {
        recvChar.health -= totalDamage;
      }

      // 3) Death check for attacker
      if (recvChar.health <= 0) {
        recvChar.health = 0;
        recvChar.isDead = true;
        recvChar.eliminatedBy = {
          name: defender.name,
          id: defender.id,
          character: defender.character.name,
          eliminatedAt: Date.now(),
        };
      }

      // 4) Write back attacker and sync move
      playersCopy[botIndex] = attacker;
      const reflectedPlayers = stripHandAndDeckFromPlayers(playersCopy);

      const move = {
        type: "attack - reflected",
        attacker: attacker.name,
        defender: defender.name,
        damage: totalDamage,
        criticalHit,
        elementalBonus,
        timestamp: Date.now(),
      };

      await syncMatchData(attacker.id, reflectedPlayers, move);
      await delay(500);
      return; // stop normal damage application
    }

    // Normal damage → apply to DEFENDER
    if (defender.character.stance === "defense") {
      if (defender.character.defense >= totalDamage) {
        defender.character.defense -= totalDamage;
      } else {
        const leftover = totalDamage - defender.character.defense;
        defender.character.defense = 0;
        defender.character.stance = "attack";
        defender.character.health -= leftover;
      }
    } else {
      defender.character.health = Math.max(
        0,
        defender.character.health - totalDamage
      );
    }

    // Death check for defender
    if (defender.character.health <= 0) {
      defender.character.health = 0;
      defender.character.isDead = true;
      defender.character.eliminatedBy = {
        name: attacker.name,
        id: attacker.id,
        character: attacker.character.name,
        eliminatedAt: Date.now(),
      };
    }
  }

  // Match human attack flow: consume instant attack/accuracy buffs after an attack attempt.
  if (!attacker.character.buffs) {
    attacker.character.buffs = {
      attack: [],
      accuracy: [],
    };
  }
  ["attack", "accuracy"].forEach((stat) => {
    if (Array.isArray(attacker.character.buffs[stat])) {
      attacker.character.buffs[stat] = attacker.character.buffs[stat].filter(
        (buff) => !(buff?.duration === 0)
      );
    } else {
      attacker.character.buffs[stat] = [];
    }
  });

  // Reset attacker luck after an attack attempt (hit, miss, or reflect)
  if (attacker?.character) {
    attacker.character.luck = 0;
  }

  // Persist attacker/defender back to playersCopy
  playersCopy[botIndex] = attacker;
  playersCopy[targetIndex] = defender;

  // (Optional) you can maintain an eliminated list here if needed, using fresh RTDB read
  const freshData = await getGameplayMatchSnapshotRTDB(roomId, {
    includeLegacyFallback: true,
    requirePlayers: true,
  });
  if (!freshData) return;

  const move = {
    type: totalDamage === 0 ? "attack - missed" : "attack",
    attacker: attacker.name,
    defender: defender.name,
    damage: totalDamage,
    criticalHit,
    elementalBonus,
    timestamp: Date.now(),
  };

  const updatedPlayersCopy = stripHandAndDeckFromPlayers(playersCopy);
  await syncMatchData(attacker.id, updatedPlayersCopy, move);
  await delay(500);
};

export const simulateBotPlayCard = async (
  roomId,
  bot,
  playersCopy,
  card,
  syncMatchData,
  target = bot,
  matchRef, // ✅ should already be RTDB ref
  hands,
  decks,
  mode = GAME_MODES.ROYALE,
  commitMatchMutation = null
) => {
  try {
    if (!isAlivePlayer(bot) || !isAlivePlayer(target)) return;

    const botIndex = playersCopy.findIndex((p) => p.id === bot.id);
    const targetIndex = playersCopy.findIndex((p) => p.id === target.id);
    if (
      botIndex < 0 ||
      targetIndex < 0 ||
      !isAlivePlayer(playersCopy[botIndex]) ||
      !isAlivePlayer(playersCopy[targetIndex])
    ) {
      return;
    }

    let reCopiedPlayers = [...playersCopy];
    let acting = { ...bot };
    let receiver = { ...target };

    acting.hand = hands[acting.id];
    acting.deck = decks[acting.id];

    // === SPECIALTY CARD LOGIC ===
    if (isSpecialtyLikeCard(card)) {
      const specialtyCardData = parseCardEffect(card);
      const isBarrierBlocked =
        specialtyCardData.isAffliction &&
        Number(receiver?.character?.magicBarrierTurns ?? 0) > 0;
      const originalReceiverHand = Array.isArray(hands?.[receiver.id])
        ? [...hands[receiver.id]]
        : [];
      const result = isBarrierBlocked
        ? {
            players: reCopiedPlayers,
            hands,
            decks,
          }
        : specialtyCardEffect({
            card,
            actingPlayer: acting,
            targetPlayer: receiver,
            players: reCopiedPlayers,
            hands,
            decks,
            matchRef,
            roomId,
            mode,
          });

      reCopiedPlayers = result.players;
      hands = result.hands;
      decks = result.decks;
      const triggeredHelpCard = result.triggeredHelpCard || null;

      // 🃏 Sleight of Hand specifics:
      if (card.name === "Sleight of Hand" || card.name === "Slight of Hand") {
        // The specialty effect already removed the played card from the bot’s hand
        // and swapped hands. Now (optionally) draw 1 for the bot like you do elsewhere:
        if (decks[acting.id]?.length > 0) {
          hands[acting.id].push(decks[acting.id].shift());
        }

        // Persist BOTH hands since both changed
        await updateHandAndDeckRTDB(
          roomId,
          acting.id,
          hands[acting.id],
          decks[acting.id]
        );
        await updateHandAndDeckRTDB(
          roomId,
          receiver.id,
          hands[receiver.id],
          decks[receiver.id]
        );
      } else {
        // Other specialty cards: normal removal/draw flow
        hands[acting.id] = hands[acting.id].filter((c) => c.id !== card.id);
        if (decks[acting.id]?.length > 0) {
          hands[acting.id].push(decks[acting.id].shift());
        }
        await updateHandAndDeckRTDB(
          roomId,
          acting.id,
          hands[acting.id],
          decks[acting.id]
        );
        const receiverHandChanged =
          receiver.id !== acting.id &&
          JSON.stringify(hands?.[receiver.id] || []) !==
            JSON.stringify(originalReceiverHand);
        if (receiverHandChanged) {
          await updateHandAndDeckRTDB(
            roomId,
            receiver.id,
            hands[receiver.id],
            decks[receiver.id]
          );
        }
      }

      // 🔄 Reversal Stone — flip turn order and consume the card
      if (card.name === "Reversal Stone") {
        const isSelfTarget = target?.id === acting?.id;
        // 1) Flip the flag in RTDB based on freshest value
        if (typeof commitMatchMutation === "function") {
          await commitMatchMutation({
            source: "bot:playCard:reversalStone",
            actorId: bot?.id || null,
            targetId: target?.id || null,
            mutate: (fresh) => ({
              reversed: !Boolean(fresh?.reversed),
              pendingExtraTurnPlayerId: isSelfTarget
                ? acting?.id || null
                : fresh?.pendingExtraTurnPlayerId || null,
              message: `${bot.name} used Reversal Stone! Turn order has been ${
                fresh?.reversed ? "restored" : "reversed"
              }${isSelfTarget ? " and gains an extra turn" : ""}.`,
            }),
          });
        } else {
          const fresh =
            (await getGameplayMatchSnapshotRTDB(roomId, {
              includeLegacyFallback: true,
              requirePlayers: true,
            })) || {};
          await update(matchRef, {
            reversed: !Boolean(fresh.reversed),
            pendingExtraTurnPlayerId: isSelfTarget
              ? acting?.id || null
              : fresh?.pendingExtraTurnPlayerId || null,
            message: `${bot.name} used Reversal Stone! Turn order has been ${
              fresh.reversed ? "restored" : "reversed"
            }${isSelfTarget ? " and gains an extra turn" : ""}.`,
          });
        }

        // 2) Remove the card from bot's hand and (optionally) draw 1
        hands[acting.id] = (hands[acting.id] || []).filter(
          (c) => c.id !== card.id
        );
        if ((decks[acting.id] || []).length > 0) {
          hands[acting.id].push(decks[acting.id].shift());
        }

        // 3) Persist bot hand/deck
        await updateHandAndDeckRTDB(
          roomId,
          acting.id,
          hands[acting.id],
          decks[acting.id]
        );

        // 4) Log the move
        const cleanPlayers = stripHandAndDeckFromPlayers(reCopiedPlayers);
        const move = {
          type: "playCard",
          player: bot.name,
          card,
          target: target.name,
          timestamp: Date.now(),
        };
        await syncMatchData(bot.id, cleanPlayers, move);
        await delay(500);
        return;
      }

      const cleanPlayers = stripHandAndDeckFromPlayers(reCopiedPlayers);

      const move = {
        type: "playCard",
        player: bot.name,
        card,
        target: target.name,
        preventedByMagicBarrier:
          specialtyCardData.isAffliction && isBarrierBlocked,
        reactionCard: triggeredHelpCard,
        timestamp: Date.now(),
      };

      await syncMatchData(bot.id, cleanPlayers, move);
      await delay(500);
      return;
    }

    // === NORMAL CARD LOGIC ===
    const { affectedAreas, amount, duration, isAid, isAffliction } =
      parseCardEffect(card);
    let triggeredHelpCard = null;
    const isBarrierBlocked =
      isAffliction && Number(receiver?.character?.magicBarrierTurns ?? 0) > 0;
    const incomingHealthDamage = affectedAreas.some(
      (key) => normalizeEffectArea(key) === "health"
    ) && Number(amount) < 0;

    if (!isBarrierBlocked) {
      if (incomingHealthDamage) {
        const helpResult = maybeActivateCurseAbsorptionFromHand({
          recipient: receiver,
          hands,
        });
        if (helpResult.activated) {
          hands = helpResult.hands;
          triggeredHelpCard = helpResult.consumedCard || null;
          receiver.character = {
            ...receiver.character,
            curseAbsorptionTurns: Math.max(
              Number(receiver.character?.curseAbsorptionTurns || 0),
              helpResult.duration
            ),
          };
          await updateHandAndDeckRTDB(
            roomId,
            receiver.id,
            hands[receiver.id],
            decks?.[receiver.id] || []
          );
          if (receiver.id === acting.id) {
            acting.hand = hands[acting.id];
          }
        }
      }
    

      affectedAreas.forEach((key) => {
      const area = normalizeEffectArea(key);
      const appliedAmount =
        area === "health"
          ? resolveCurseAbsorptionHealthAmount(receiver.character, amount)
          : amount;
      const safeAppliedAmount = toFiniteNumber(appliedAmount, 0);
      const isInstant = duration === 0;

      if (isInstant && area !== "attack" && area !== "accuracy") {
        const initValue = toFiniteNumber(receiver.character?.[area], 0);
        receiver.character[area] = initValue + safeAppliedAmount;

        // 🔥 Death check for instant health loss
        if (
          area === "health" &&
          receiver.character.health <= 0 &&
          !receiver.character.isDead
        ) {
          receiver.character.health = 0;
          receiver.character.isDead = true;
          receiver.character.eliminatedBy = {
            name: bot.name,
            id: bot.id,
            character: bot.character.name,
            eliminatedAt: Date.now(),
          };
        }

        if (area === "defense" && receiver.character.defense <= 0) {
          receiver.character.stance = "attack";
        }
      } else {
        if (!receiver.character) receiver.character = {};
        if (!receiver.character.buffs) receiver.character.buffs = {};
        if (!receiver.character.buffs[area]) {
          receiver.character.buffs[area] = [];
        }

        if (area === "health" || area === "defense") {
          const initValue = toFiniteNumber(receiver.character?.[area], 0);
          receiver.character[area] = initValue + safeAppliedAmount;

          if (area === "defense" && receiver.character.defense <= 0) {
            receiver.character.stance = "attack";
          }

          // 🔥 Death check for instant health loss
          if (
            area === "health" &&
            receiver.character.health <= 0 &&
            !receiver.character.isDead
          ) {
            receiver.character.health = 0;
            receiver.character.isDead = true;
            receiver.character.eliminatedBy = {
              name: bot.name,
              id: bot.id,
              character: bot.character.name,
              eliminatedAt: Date.now(),
            };
          }
        }

        const buffObj = buildOngoingCardBuff({
          area,
          amount,
          duration,
          card,
          sourcePlayer: bot,
        });
        if (buffObj) {
          receiver.character.buffs[area].push(buffObj);
        }
      }
      });
    }

    // Remove card from bot’s hand
    acting.hand = acting.hand.filter((c) => c.id !== card.id);

    // Refill hand if deck isn't empty
    if (acting?.deck?.length > 0) {
      acting.hand.push(acting.deck.shift());
    }

    // Save updated hand/deck to RTDB
    await updateHandAndDeckRTDB(roomId, acting.id, acting.hand, acting.deck);

    // Update players copy for sync
    reCopiedPlayers[botIndex] = acting;
    reCopiedPlayers[targetIndex] = receiver;

    const move = {
      type: "playCard",
      player: bot.name,
      card,
      target: target.name,
      effect: { affectedAreas, amount, duration, isAid },
      preventedByMagicBarrier: isAffliction && isBarrierBlocked,
      reactionCard: triggeredHelpCard,
      timestamp: Date.now(),
    };

    const updatedPlayersCopy = stripHandAndDeckFromPlayers(reCopiedPlayers);
    await syncMatchData(bot.id, updatedPlayersCopy, move);
    await delay(500);
  } catch (err) {
    console.error("❌ simulateBotPlayCard Error:", err);
  }
};

export const simulateBotTradeCard = async (
  roomId,
  bot,
  target,
  playersCopy,
  card,
  syncMatchData,
  hands,
  decks
) => {
  if (!isAlivePlayer(bot) || !isAlivePlayer(target)) return false;

  const botIndex = playersCopy.findIndex((p) => p.id === bot.id);
  const targetIndex = playersCopy.findIndex((p) => p.id === target.id);
  if (
    botIndex < 0 ||
    targetIndex < 0 ||
    !isAlivePlayer(playersCopy[botIndex]) ||
    !isAlivePlayer(playersCopy[targetIndex])
  ) {
    return false;
  }

  const botCopy = { ...bot };
  let botHand = hands?.[bot.id];
  const targetCopy = { ...target };
  const targetPlayerHand = hands?.[target.id];

  if (!botHand?.length || !targetPlayerHand?.length) {
    console.warn("🛑 Cannot trade — one or both players have no cards.");
    return false;
  }

  // Find weakest card from target to trade for
  const lowestCard = targetPlayerHand.reduce((min, c) =>
    (c.amount ?? 0) < (min.amount ?? 0) ? c : min
  );

  // Remove exactly one traded instance from each hand
  botHand = removeOneCardInstance(botHand, card);
  targetCopy.hand = removeOneCardInstance(targetPlayerHand, lowestCard);

  // Execute the trade
  botHand.push(lowestCard);
  targetCopy.hand.push(card);

  botCopy.hand = botHand;

  try {
    await Promise.all([
      updateHandAndDeckRTDB(roomId, bot.id, botHand, null),
      updateHandAndDeckRTDB(roomId, targetCopy.id, targetCopy.hand, null),
    ]);
  } catch (err) {
    console.error("🚨 Failed to save deck/hand during trade:", err);
    return false;
  }

  // Update players
  playersCopy[botIndex] = botCopy;
  playersCopy[targetIndex] = targetCopy;

  const move = {
    type: "tradeCard",
    player: bot.name,
    card,
    target: target.name,
    timestamp: Date.now(),
  };

  const updatedPlayersCopy = stripHandAndDeckFromPlayers(playersCopy);
  await syncMatchData(bot.id, updatedPlayersCopy, move);
  await delay(500);

  return true;
};
